import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ListAnnouncementsParams } from "./client";
import { announcementsApi } from "./client";

export const announcementKeys = {
  all: ["announcements"] as const,
  list: (params?: ListAnnouncementsParams) =>
    [...announcementKeys.all, "list", params] as const,
  detail: (id: string) => [...announcementKeys.all, "detail", id] as const,
  pushMonitor: (id: string) =>
    [...announcementKeys.all, "push-monitor", id] as const,
};

export function useAnnouncementsQuery(params?: ListAnnouncementsParams) {
  return useQuery({
    queryKey: announcementKeys.list(params),
    queryFn: () => announcementsApi.list(params),
  });
}

export function useAnnouncementQuery(id: string) {
  return useQuery({
    queryKey: announcementKeys.detail(id),
    queryFn: () => announcementsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateAnnouncementMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: announcementsApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: announcementKeys.all });
    },
  });
}

export function useUpdateAnnouncementMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Parameters<typeof announcementsApi.update>[1];
    }) => announcementsApi.update(id, payload),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: announcementKeys.detail(id) });
      qc.invalidateQueries({ queryKey: announcementKeys.all });
    },
  });
}

export function useToggleAnnouncementActiveMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      announcementsApi.toggleActive(id, isActive),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: announcementKeys.detail(id) });
      qc.invalidateQueries({ queryKey: announcementKeys.all });
    },
  });
}

export function useResendAnnouncementPushMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: announcementsApi.resendPush,
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: announcementKeys.detail(id) });
      qc.invalidateQueries({ queryKey: announcementKeys.pushMonitor(id) });
      qc.invalidateQueries({ queryKey: announcementKeys.all });
    },
  });
}

export function useAnnouncementPushMonitorQuery(id: string) {
  return useQuery({
    queryKey: announcementKeys.pushMonitor(id),
    queryFn: () => announcementsApi.pushMonitor(id),
    enabled: !!id,
    refetchInterval: (query) => {
      const d = query.state.data;
      if (d?.hasPendingOutbox) return 3_000;
      return false;
    },
  });
}

export function useDeleteAnnouncementMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: announcementsApi.delete,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: announcementKeys.all });
    },
  });
}
